import { useMemo, useState } from 'react'

import type { IssueSeverity } from '@/shared/constants/issue-status'

import { DonutCard } from './DashboardCards'
import { DashboardFilterBar, type FilterChip } from './DashboardFilterBar'
import { DashboardKpiRow } from './DashboardKpiRow'
import { DistrictScorecard } from './DistrictScorecard'
import { IssueAgingCard } from './IssueAgingCard'
import { IssueDimensionCard } from './IssueDimensionCard'
import { IssueTypeCard } from './IssueTypeCard'
import { useBoardShortcuts } from '../../../hooks/useBoardShortcuts'
import { useDashboardInsights } from '../../../hooks/useDashboardInsights'
import { useHodDirectory } from '../../../hooks/useHodDirectory'
import { EMPTY_DASH_FILTER, dashFilterActive, type DashFilter } from '../../../lib/dashboard-filter'
import { ageBandShort } from '../../../lib/dashboard-insights'
import { filterByFacets, type IssueFacets } from '../../../lib/issue-board'
import { isActive } from '../../../lib/issues'

import type { GisOverview, GisScope, GisTab } from '../../../api/types'

interface DashboardTabProps {
  overview: GisOverview
  scope: GisScope
  /** Page-level mandal (taluka) filter — every dashboard module re-scopes to it. */
  activeMandals: readonly string[]
  onOpenLens: (tab: GisTab) => void
  onSelectMandal: (name: string) => void
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * The **Dashboard** tab (V7) — an optimized operational read with a staged multi-select filter.
 * Every chart (age bands · importance donut · type donut · Department/HOD/Site breakdown) toggles
 * values into ONE dashboard filter; the **KPI cards recompute live** over it (via the board's own
 * `filterByFacets`), the **filter bar** shows the selection + Clear + "Show issues →", and Show
 * issues writes the filter to the board store and navigates. Taluka stays the page mandal scope
 * (re-scopes the whole tab). The scorecard rounds out the read.
 */
export function DashboardTab({ overview, scope, activeMandals, onOpenLens, onSelectMandal }: DashboardTabProps) {
  const i = useDashboardInsights(overview, scope, activeMandals)
  const go = useBoardShortcuts({ onOpenLens, onSelectMandal, activeMandals })
  const { data: directory } = useHodDirectory()

  const [filter, setFilter] = useState<DashFilter>(EMPTY_DASH_FILTER)
  const toggle = (dim: keyof DashFilter, value: string): void =>
    setFilter((f) => {
      const cur = f[dim] as readonly string[]
      const next = cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]
      return { ...f, [dim]: next } as DashFilter
    })
  const active = dashFilterActive(filter)

  const siteMandal = useMemo(() => new Map(overview.sites.map((s) => [s.id, s.mandal])), [overview.sites])

  // KPIs recompute over the filter using the board's own engine. A filter that includes age is
  // open-only (age = pending duration → Completed collapses to 0); otherwise it spans open + closed.
  const kpi = useMemo(() => {
    if (!active) {
      return {
        totalSites: i.siteTotal,
        totalIssues: i.totalIssues,
        completed: i.closedInPeriod,
        pending: i.signals.unresolved,
      }
    }
    const hasAge = filter.age.length > 0
    const base = hasAge ? i.openIssues : i.scopedIssues
    const facets: IssueFacets = {
      statusView: 'all',
      severity: filter.severity,
      category: filter.category,
      entity: [],
      department: filter.department,
      hod: filter.hod,
      mandal: [],
      age: filter.age,
      site: filter.site,
    }
    const f = filterByFacets(base, facets, siteMandal)
    const pending = f.filter(isActive).length
    const totalSites = new Set(f.map((x) => x.siteId).filter((s): s is string => s !== null)).size
    return { totalSites, totalIssues: f.length, completed: f.length - pending, pending }
  }, [active, filter, i.siteTotal, i.totalIssues, i.closedInPeriod, i.signals.unresolved, i.openIssues, i.scopedIssues, siteMandal])

  const hodName = (dep: string): string => directory?.find((d) => d.department === dep)?.hodName ?? dep
  const chips: FilterChip[] = [
    ...filter.age.map((k) => ({ id: `age:${k}`, label: `Age: ${ageBandShort(k)}`, onRemove: () => toggle('age', k) })),
    ...filter.severity.map((k) => ({ id: `sev:${k}`, label: `Importance: ${cap(k)}`, onRemove: () => toggle('severity', k) })),
    ...filter.category.map((k) => ({ id: `cat:${k}`, label: `Type: ${k}`, onRemove: () => toggle('category', k) })),
    ...filter.department.map((k) => ({ id: `dep:${k}`, label: `Dept: ${k}`, onRemove: () => toggle('department', k) })),
    ...filter.hod.map((k) => ({ id: `hod:${k}`, label: `HOD: ${hodName(k)}`, onRemove: () => toggle('hod', k) })),
    ...filter.site.map((k) => ({ id: `site:${k}`, label: `Site: ${k}`, onRemove: () => toggle('site', k) })),
  ]

  return (
    <div className="@container/dash flex flex-col gap-3">
      <DashboardKpiRow
        totalSites={kpi.totalSites}
        totalIssues={kpi.totalIssues}
        completed={kpi.completed}
        pending={kpi.pending}
        loading={i.issuesPending}
        filtered={active}
        go={go}
      />

      <DashboardFilterBar chips={chips} onClear={() => setFilter(EMPTY_DASH_FILTER)} onShow={() => go.goIssuesFiltered(filter)} />

      <div className="grid grid-cols-1 gap-3 @[820px]/dash:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <IssueDimensionCard
          openIssues={i.openIssues}
          sites={i.scopedSites}
          mandalCount={i.scopedMandals.length}
          pending={i.issuesPending}
          selected={{ department: filter.department, hod: filter.hod, site: filter.site, mandal: activeMandals }}
          onToggle={(dim, value) => (dim === 'mandal' ? onSelectMandal(value) : toggle(dim, value))}
          go={go}
        />
        <IssueAgingCard
          openIssues={i.openIssues}
          pending={i.issuesPending}
          selected={filter.age}
          onToggle={(k) => toggle('age', k)}
          go={go}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 @[820px]/dash:grid-cols-2">
        <DonutCard
          title="Issues by importance"
          segments={i.sevSegments}
          active={filter.severity}
          centerValue={i.issuesPending ? '—' : String(i.signals.unresolved)}
          centerLabel="OPEN"
          onPick={(key) => toggle('severity', key as IssueSeverity)}
          onOpen={() => go.goIssues()}
          openLabel="Open Issues board"
        />
        <IssueTypeCard
          openIssues={i.openIssues}
          pending={i.issuesPending}
          selected={filter.category}
          onToggle={(k) => toggle('category', k)}
          go={go}
        />
      </div>

      <DistrictScorecard mandals={i.scopedMandals} activeMandals={activeMandals} onPickMandal={onSelectMandal} />
    </div>
  )
}
